import { BuyerUserRole } from '@choco-admin/types';
import { Avatar } from '@heroui/react';
import React, { useState, useCallback } from 'react';

import { BaseButton } from '../../../../components/BaseButton';
import {
  EntitySearchModal,
  type EntitySearchOption,
  type RoleOption,
} from '../../../../components/FormComponents';
import { useBuyerLinking } from '../../hooks/useBuyerLinking';
import { useBuyerSearch } from '../../hooks/useBuyerSearch';

import { BuyerSelectOption, ROLE_LABELS } from './types';

interface LinkBuyerModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  linkedBuyers: BuyerSelectOption[];
  onSuccess?: (newLinkedBuyer: BuyerSelectOption) => void;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    value: BuyerUserRole.ADMIN,
    label: ROLE_LABELS[BuyerUserRole.ADMIN],
    description: 'Full access to all buyer features',
  },
  {
    value: BuyerUserRole.STAFF,
    label: ROLE_LABELS[BuyerUserRole.STAFF],
    description: 'Standard user access',
  },
  {
    value: BuyerUserRole.CONTRIBUTOR,
    label: ROLE_LABELS[BuyerUserRole.CONTRIBUTOR],
    description: 'Limited access for external contributors',
  },
  {
    value: BuyerUserRole.SUPPORT,
    label: ROLE_LABELS[BuyerUserRole.SUPPORT],
    description: 'Support access for troubleshooting',
  },
];

export const LinkBuyerModal: React.FC<LinkBuyerModalProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  linkedBuyers,
  onSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);

  const {
    isLoading: searchLoading,
    options: searchResults,
    searchBuyers,
  } = useBuyerSearch();
  const { linkUserToBuyer, isLinking } = useBuyerLinking();

  // Filter out already linked buyers and convert to EntitySearchOption format
  const linkedBuyerIds = new Set(linkedBuyers.map((buyer) => buyer.value));
  const availableBuyers: EntitySearchOption[] = searchResults
    .filter((buyer) => !linkedBuyerIds.has(buyer.value))
    .map((buyer) => {
      // Parse the label format: "${name}-${referenceId} (${address})"
      const { label } = buyer;
      const addressMatch = label.match(/\(([^)]+)\)$/);
      const address = addressMatch
        ? addressMatch[1]
        : buyer.metaData?.fullAddress || '';
      const withoutAddress = addressMatch
        ? label.replace(/\s*\([^)]+\)$/, '').trim()
        : label;

      // Split by last dash to separate name and refID
      // Format: "name-refID" -> name is everything before last dash, refID is after
      const lastDashIndex = withoutAddress.lastIndexOf('-');
      const buyerName =
        lastDashIndex > 0
          ? withoutAddress.substring(0, lastDashIndex)
          : withoutAddress;
      const referenceId =
        lastDashIndex > 0 ? withoutAddress.substring(lastDashIndex + 1) : '';

      return {
        id: buyer.value,
        name: buyerName || buyer.label, // Fallback to full label if parsing fails
        label: buyer.label,
        subtitle: referenceId || undefined, // Store refID in subtitle temporarily
        metadata: {
          ...buyer.metaData,
          fullAddress: address,
          buyerReferenceId: referenceId, // Store separately for easy access
          buyerName,
        },
      };
    });

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.trim().length > 2) {
        searchBuyers(searchTerm.trim());
      }
    },
    [searchBuyers]
  );

  const handleConfirm = async (
    selectedEntity: EntitySearchOption,
    role?: string
  ) => {
    setError(null);

    // Convert back to BuyerSelectOption format
    const buyerWithRole: BuyerSelectOption = {
      value: selectedEntity.id,
      label: selectedEntity.label,
      metaData: {
        ...selectedEntity.metadata,
        buyerUserRole: role as BuyerUserRole,
      },
    };

    try {
      // If we have a non-empty userId, this is an edit flow - call the mutation
      if (userId && userId !== '') {
        await linkUserToBuyer(buyerWithRole, userId);
      }

      // Always call onSuccess with the selected buyer
      onSuccess?.(buyerWithRole);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link buyer');
    }
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  const renderEntity = (
    entity: EntitySearchOption,
    onSelect: (entity: EntitySearchOption) => void
  ) => {
    const buyerName = (entity.metadata as any)?.buyerName || entity.name;
    const referenceId = (entity.metadata as any)?.buyerReferenceId || '';
    const address = entity.metadata?.fullAddress || '';

    return (
      <BaseButton
        key={entity.id}
        variant="light"
        fullWidth
        className="flex items-start gap-3 p-3 h-auto min-h-[70px]"
        onPress={() => onSelect(entity)}
      >
        <Avatar
          name={buyerName?.charAt(0) || entity.id.charAt(0)}
          size="sm"
          color="primary"
          className="flex-shrink-0 mt-0.5"
        />
        <div className="flex-1 text-left min-w-0 flex flex-col gap-1.5">
          {/* Line 1: Buyer Name */}
          <p className="font-semibold text-foreground text-sm leading-tight break-words">
            {buyerName}
          </p>

          {/* Line 2: Reference ID */}
          {referenceId && (
            <p className="text-xs text-default-400 font-mono leading-tight">
              Ref: {referenceId}
            </p>
          )}

          {/* Line 3: Address */}
          {address && (
            <p className="text-xs text-default-500 leading-tight break-words">
              {address}
            </p>
          )}
        </div>
      </BaseButton>
    );
  };

  return (
    <EntitySearchModal
      isOpen={isOpen}
      onClose={onClose}
      title="Link User to Buyer"
      searchPlaceholder="Search buyers by name or reference ID..."
      entityType="buyer"
      isSearching={searchLoading}
      searchResults={availableBuyers}
      onSearch={handleSearch}
      onConfirm={handleConfirm}
      isConfirming={isLinking}
      roleOptions={ROLE_OPTIONS}
      defaultRole={BuyerUserRole.STAFF}
      error={error}
      onErrorDismiss={handleErrorDismiss}
      renderEntity={renderEntity}
    />
  );
};
